import React, { createContext, useContext, useState, useCallback } from 'react';
import type { SettingType } from '@/hooks/useFeatures.constants';

export type DemoRole = 'therapeut' | 'patient';

type DemoContextType = {
  isDemo: boolean;
  demoRole: DemoRole | null;
  demoSettingType: SettingType | null;
  startDemo: (role: DemoRole, settingType?: SettingType) => void;
  exitDemo: () => void;
};

const DemoContext = createContext<DemoContextType>({
  isDemo: false,
  demoRole: null,
  demoSettingType: null,
  startDemo: () => {},
  exitDemo: () => {},
});

const DEMO_KEY         = 'nutriflow-demo-active';
const DEMO_ROLE_KEY    = 'nutriflow-demo-role';
const DEMO_SETTING_KEY = 'nutriflow-demo-setting-type';

export const DemoProvider = ({ children }: { children: React.ReactNode }) => {
  const [isDemo, setIsDemo] = useState(
    () => sessionStorage.getItem(DEMO_KEY) === 'true'
  );
  const [demoRole, setDemoRole] = useState<DemoRole | null>(
    () => (sessionStorage.getItem(DEMO_ROLE_KEY) as DemoRole) || null
  );
  const [demoSettingType, setDemoSettingType] = useState<SettingType | null>(
    () => (sessionStorage.getItem(DEMO_SETTING_KEY) as SettingType) || null
  );

  const startDemo = useCallback((role: DemoRole, settingType: SettingType = 'praxis') => {
    sessionStorage.setItem(DEMO_KEY,         'true');
    sessionStorage.setItem(DEMO_ROLE_KEY,    role);
    sessionStorage.setItem(DEMO_SETTING_KEY, settingType);
    setIsDemo(true);
    setDemoRole(role);
    setDemoSettingType(settingType);
  }, []);

  const exitDemo = useCallback(() => {
    sessionStorage.removeItem(DEMO_KEY);
    sessionStorage.removeItem(DEMO_ROLE_KEY);
    sessionStorage.removeItem(DEMO_SETTING_KEY);
    setIsDemo(false);
    setDemoRole(null);
    setDemoSettingType(null);
  }, []);

  return (
    <DemoContext.Provider value={{ isDemo, demoRole, demoSettingType, startDemo, exitDemo }}>
      {children}
    </DemoContext.Provider>
  );
};

export const useDemo = () => useContext(DemoContext);
